import { openDB, IDBPDatabase } from 'idb'

export interface HistoryEntry {
  id: string
  url: string
  title: string
  thumbnail?: string
  provider?: string
  type?: string
  lastPosition: number
  duration?: number
  visitedAt: string
}

const DB_NAME = '777player'
const DB_VERSION = 1
const STORE = 'history'

let dbPromise: Promise<IDBPDatabase> | null = null

function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'id' })
          store.createIndex('visitedAt', 'visitedAt', { unique: false })
        }
      }
    })
  }
  return dbPromise
}

export async function addHistoryEntry(entry: HistoryEntry): Promise<void> {
  const db = await getDb()
  await db.put(STORE, entry)
  // Keep only last 50 entries
  const all = await db.getAllFromIndex(STORE, 'visitedAt')
  if (all.length > 50) {
    const toDelete = all.slice(0, all.length - 50)
    const tx = db.transaction(STORE, 'readwrite')
    await Promise.all(toDelete.map((e: HistoryEntry) => tx.store.delete(e.id)))
    await tx.done
  }
}

export async function getHistory(): Promise<HistoryEntry[]> {
  try {
    const db = await getDb()
    const all = await db.getAllFromIndex(STORE, 'visitedAt')
    return all.reverse() as HistoryEntry[]
  } catch {
    return []
  }
}

export async function updatePosition(id: string, position: number, duration?: number): Promise<void> {
  try {
    const db = await getDb()
    const entry = await db.get(STORE, id) as HistoryEntry | undefined
    if (entry) {
      entry.lastPosition = position
      if (duration) entry.duration = duration
      await db.put(STORE, entry)
    }
  } catch { /* silent */ }
}

export async function clearHistory(): Promise<void> {
  const db = await getDb()
  await db.clear(STORE)
}

export async function deleteEntry(id: string): Promise<void> {
  const db = await getDb()
  await db.delete(STORE, id)
}

export function generateId(url: string): string {
  return btoa(url).replace(/[^a-z0-9]/gi, '').slice(0, 20)
}
